import React, { Fragment, useState } from 'react';
import styles from './styles.module.scss';
import Typography from '@components/common/typography';
import { useNavigate } from 'react-router-dom';
import Tabs from '@components/tabs';
import Button from '@components/common/button';
import Input from '@components/common/input';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiAdminService } from '@api';
import { useForm } from '@hooks/helpers/useForm';
import FormField from '@components/form_field';
import { FormProvider, useWatch } from 'react-hook-form';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { ELoyaltyLevel, TLoyaltySettings } from '@xeppt/xeppt-sdk/types/loyalty';
import { toast } from 'react-toastify';
import { useLocales } from '@hooks/helpers/useLocales';
import { convertStringsToNumbers } from '@utils/index';
import { BUSINESS_ONLY_OPERATIONS, EFundsOperation, INDIVIDUAL_ONLY_OPERATIONS } from '@enum';
import { EUserType } from '@xeppt/xeppt-sdk/types/user';

// Transaction-type labels MUST match the consumer portal (xeppt-portal loyalty
// operationNames) and the admin Limits tab — keep all three in sync.
export const paymentMethods = [
    { key: EFundsOperation.WALLET_CREDIT_TRANSFER, label: 'Send money via XEPPT' },
    { key: EFundsOperation.WALLET_DEBIT_TRANSFER, label: 'Receive money via XEPPT' },
    { key: EFundsOperation.WALLET_DEBIT_PAYMENT, label: 'Receive a Pay with XEPPT payment' },
    { key: EFundsOperation.WALLET_CREDIT_REQUEST, label: 'Approve someone’s money request' },
    { key: EFundsOperation.WALLET_DEBIT_REQUEST, label: 'Request money via XEPPT (when approved)' },
    { key: EFundsOperation.WALLET_CREDIT_CARD_DEPOSIT, label: 'Load XEPPT card balance' },
    // { key: EFundsOperation.WALLET_CREDIT_CARD_SERVICE, label: 'Order XEPPT card' },
    { key: EFundsOperation.WALLET_DEBIT_CASHBACK, label: 'Redeem cashback' },
    { key: EFundsOperation.PAY_BILL_CREDIT_PAYMENT, label: 'Pay a bill' },
    {
        key: EFundsOperation.E_TRANSFER_CREDIT_TRANSFER,
        label: 'Send money via Interac e-Transfer®'
    },
    {
        key: EFundsOperation.E_TRANSFER_DEBIT_TRANSFER,
        label: 'Receive money via Interac e-Transfer®'
    },
    {
        key: EFundsOperation.E_TRANSFER_DEBIT_REQUEST,
        label: 'Request money via Interac e-Transfer® (when approved)'
    },
    { key: EFundsOperation.EFT_CREDIT_WITHDRAWAL, label: 'Withdraw to linked bank account' },
    { key: EFundsOperation.EFT_DEBIT_DEPOSIT, label: 'Load balance from linked bank account' },
    { key: EFundsOperation.EPS_DEBIT_DEPOSIT, label: 'Load balance from linked card' },
    { key: EFundsOperation.CARD_CREDIT_WITHDRAWAL, label: 'Pay with XEPPT card' },
    { key: EFundsOperation.CARD_DEBIT_DEPOSIT, label: 'XEPPT card load balance' },
    { key: EFundsOperation.GIFT_CARD_DEBIT_GIFT_CARD_REDEEM, label: 'Redeem XEPPT gift card' },
    { key: EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD, label: 'Issue XEPPT gift card' },
    { key: EFundsOperation.WALLET_CREDIT_PAYMENT, label: 'Make a Pay with XEPPT payment' },
    // Payout legs follow this file's CREDIT = money OUT / DEBIT = money IN convention
    // (as with the TRANSFER/PAYMENT/EFT rows above, and backend
    // models/transaction_types.go): WALLET_CREDIT_PAYOUT (1_1_16) is the merchant's
    // outbound send leg — business-only, gated in BUSINESS_ONLY_OPERATIONS;
    // WALLET_DEBIT_PAYOUT (1_2_16) is the customer's inbound receive leg, which
    // individuals do use, so it stays on the Personal tab.
    { key: EFundsOperation.WALLET_CREDIT_PAYOUT, label: 'Send a payout (merchant)' },
    { key: EFundsOperation.WALLET_DEBIT_PAYOUT, label: 'Receive a payout' },
    // Merchant Connect/PWX order refund (backend-enforced business-only, key 1_2_1) —
    // see BUSINESS_ONLY_OPERATIONS in @enum for backend citations.
    {
        key: EFundsOperation.WALLET_DEBIT_REFUND,
        label: 'Refund a Pay with XEPPT payment (merchant)'
    }
];

// Individuals cannot reach any operation in BUSINESS_ONLY_OPERATIONS
// (backend-enforced 403), so those rows are hidden on the Personal tab (this
// screen has no reserve/fee row for gift cards). Every other row — including
// gift-card redeem and the Connect purchase/payout-receive legs, which
// individuals do use — is shown for both user types.
// Mirror rule: Businesses cannot reach any operation in
// INDIVIDUAL_ONLY_OPERATIONS (cashback redeem is Individual-only, backend
// ErrUserTypeIndividualAllowed — see @enum), so those rows are hidden on the
// Business tab.
export const getPaymentMethodsForUserType = (userType: EUserType) => {
    if (userType === EUserType.INDIVIDUAL) {
        return paymentMethods.filter((item) => !BUSINESS_ONLY_OPERATIONS.includes(item.key));
    }
    return paymentMethods.filter((item) => !INDIVIDUAL_ONLY_OPERATIONS.includes(item.key));
};

// Fee inputs the backend never reads — dead config that would mislead an admin into
// thinking it takes effect. Only applies on the Fees tab (feePointType === 'fee');
// the Earning points tab is never affected.
// - GIFT_CARD_CREDIT_GIFT_CARD (percent only): gift-card issue fees are flat-only
//   (a fixed price, not a % of face value).
// - WALLET_DEBIT_CASHBACK (both flat and percent): cashback redeem has no fee —
//   RedeemAtomic (cashback_service.go) credits the wallet for exactly the redeemed
//   cashback amount; the backend never reads a Fee on this key. Only its
//   points/earning row and its Limits-tab min/max are real knobs.
export const isFeeInputHidden = (
    key: EFundsOperation,
    input: 'flat' | 'percent',
    feePointType: 'fee' | 'point'
): boolean => {
    if (feePointType !== 'fee') return false;
    if (key === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD) return input === 'percent';
    if (key === EFundsOperation.WALLET_DEBIT_CASHBACK) return true;
    return false;
};
// Helper function to get only numeric values from enum
const getEnumNumericValues = (enumObject: any): number[] => {
    return Object.values(enumObject).filter((value) => typeof value === 'number') as number[];
};

const LoyaltyLayout = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<EUserType>(EUserType.INDIVIDUAL);
    const [activeTabFeePont, setActiveTabFeePont] = useState<'fee' | 'point'>('point');
    const [activeLevel, setActiveLevel] = useState<ELoyaltyLevel>(ELoyaltyLevel.A);
    const { requestErrorLocale } = useLocales();
    const [isEdit, setIsEdit] = useState(false);
    const form = useForm({});

    const { data: loyalty, handleRequest: refetchLoyalty } = useApiQuery({
        method: () => apiAdminService.getLoyaltySettings(),
        onSuccess: (data) => {
            form.reset(data);
        }
    });

    const { handleRequest: handleUpdateSettings, isLoading } = useApiMutation({
        method: () =>
            apiAdminService.updateLoyaltySettings(
                convertStringsToNumbers(form.getValues()) as TLoyaltySettings
            ),
        onSuccess: () => {
            setIsEdit(false);
            toast.success('Settings were successfully updated');
            refetchLoyalty();
        },
        onError: requestErrorLocale
    });

    const sendFlat = useWatch({ control: form.control, name: `${activeTab}.plans.${activeLevel}.${EFundsOperation.WALLET_CREDIT_TRANSFER}.flat` });
    const sendPercent = useWatch({ control: form.control, name: `${activeTab}.plans.${activeLevel}.${EFundsOperation.WALLET_CREDIT_TRANSFER}.percent` });
    const receiveFlat = useWatch({ control: form.control, name: `${activeTab}.plans.${activeLevel}.${EFundsOperation.WALLET_DEBIT_TRANSFER}.flat` });
    const receivePercent = useWatch({ control: form.control, name: `${activeTab}.plans.${activeLevel}.${EFundsOperation.WALLET_DEBIT_TRANSFER}.percent` });
    const sendHasFee = Number(sendFlat) > 0 || Number(sendPercent) > 0;
    const receiveHasFee = Number(receiveFlat) > 0 || Number(receivePercent) > 0;
    const bothHaveFees = sendHasFee && receiveHasFee;

    // Get only numeric values from enums
    const userTypes = getEnumNumericValues(EUserType) as EUserType[];
    const loyaltyLevels = getEnumNumericValues(ELoyaltyLevel) as ELoyaltyLevel[];
    const feePointTypes = ['fee', 'point'] as const;

    const handleSaveWithValidation = () => {
        const values = form.getValues() as any;
        const original = loyalty as any;
        for (const ut of userTypes) {
            for (const lvl of loyaltyLevels) {
                const plans = values?.[ut]?.plans?.[lvl];
                const sendFlatVal = Number(plans?.[EFundsOperation.WALLET_CREDIT_TRANSFER]?.flat) || 0;
                const sendPercentVal = Number(plans?.[EFundsOperation.WALLET_CREDIT_TRANSFER]?.percent) || 0;
                const receiveFlatVal = Number(plans?.[EFundsOperation.WALLET_DEBIT_TRANSFER]?.flat) || 0;
                const receivePercentVal = Number(plans?.[EFundsOperation.WALLET_DEBIT_TRANSFER]?.percent) || 0;
                const hasBoth =
                    (sendFlatVal > 0 || sendPercentVal > 0) &&
                    (receiveFlatVal > 0 || receivePercentVal > 0);
                if (!hasBoth) continue;
                // Only block when the admin actually CHANGED this tier's Send/Receive
                // fees in this session. A pre-existing both-fees tier in the loaded data
                // must NOT block unrelated edits (e.g. the reminder-email days) — that
                // soft-locks the whole page over legacy fee data the admin never touched.
                const orig = original?.[ut]?.plans?.[lvl];
                const feesChanged =
                    sendFlatVal !== (Number(orig?.[EFundsOperation.WALLET_CREDIT_TRANSFER]?.flat) || 0) ||
                    sendPercentVal !== (Number(orig?.[EFundsOperation.WALLET_CREDIT_TRANSFER]?.percent) || 0) ||
                    receiveFlatVal !== (Number(orig?.[EFundsOperation.WALLET_DEBIT_TRANSFER]?.flat) || 0) ||
                    receivePercentVal !== (Number(orig?.[EFundsOperation.WALLET_DEBIT_TRANSFER]?.percent) || 0);
                if (feesChanged) {
                    toast.error(
                        'Cannot save: both Send and Receive XEPPT fees are set for this tier. Clear one before saving.'
                    );
                    return;
                }
            }
        }
        handleUpdateSettings(undefined);
    };

    const renderFormField = (fieldName: string, inputProps: any = {}, disabled = false) => (
        <FormField
            name={fieldName}
            renderComponent={(props) => (
                <Input
                    readOnly={!isEdit}
                    onlyNumbers
                    full
                    placeholder="0.00"
                    fixedAmount
                    hideErrorIcon
                    {...inputProps}
                    {...props}
                    disabled={isEdit && disabled}
                />
            )}
        />
    );

    const renderTable = (
        userType: EUserType,
        level: ELoyaltyLevel,
        feePointType: 'fee' | 'point'
    ) => {
        const isActiveTable = userType === activeTab && level === activeLevel;
        return (
            <table
                className={styles.table}
                style={{
                    display:
                        activeTab === userType &&
                        activeLevel === level &&
                        activeTabFeePont === feePointType
                            ? 'table'
                            : 'none'
                }}>
                <tbody>
                    {feePointType === 'fee' && isActiveTable && bothHaveFees && (
                        <tr>
                            <td
                                colSpan={3}
                                style={{
                                    padding: '4px 16px 8px',
                                    color: '#d97706',
                                    fontSize: '13px',
                                    fontWeight: 500
                                }}>
                                Both Send and Receive fees are set — clear one; only one is allowed.
                            </td>
                        </tr>
                    )}
                    {getPaymentMethodsForUserType(userType).map((item) => {
                        const isSendRow = item.key === EFundsOperation.WALLET_CREDIT_TRANSFER;
                        const isReceiveRow = item.key === EFundsOperation.WALLET_DEBIT_TRANSFER;
                        const isWalletFeeRow = feePointType === 'fee' && (isSendRow || isReceiveRow);
                        const disableInputs =
                            feePointType === 'fee' &&
                            isActiveTable &&
                            !bothHaveFees &&
                            ((isSendRow && receiveHasFee) || (isReceiveRow && sendHasFee));
                        const isFlatHidden = isFeeInputHidden(item.key, 'flat', feePointType);
                        const isPercentHidden = isFeeInputHidden(
                            item.key,
                            'percent',
                            feePointType
                        );
                        return (
                            <tr key={item.key}>
                                <td>
                                    {item.label}
                                    {isWalletFeeRow && (
                                        <div
                                            style={{
                                                color: '#6b7280',
                                                fontSize: '12px',
                                                marginTop: '2px'
                                            }}>
                                            Send and Receive fees are mutually exclusive — set one
                                            or the other.
                                        </div>
                                    )}
                                </td>
                                <td>
                                    {!isFlatHidden &&
                                        renderFormField(
                                            `${userType}.plans.${level}.${item.key}.${feePointType === 'fee' ? 'flat' : 'points'}`,
                                            {
                                                suffix: feePointType === 'fee' ? '$' : '',
                                                prefix: feePointType === 'point' ? 'points' : ''
                                            },
                                            disableInputs
                                        )}
                                </td>
                                {feePointType === 'point' && <td>per</td>}
                                <td>
                                    {!isPercentHidden &&
                                        renderFormField(
                                            `${userType}.plans.${level}.${item.key}.${feePointType === 'fee' ? 'percent' : 'amount'}`,
                                            {
                                                suffix: feePointType === 'fee' ? '%' : '',
                                                prefix: feePointType === 'point' ? 'CAD' : ''
                                            },
                                            disableInputs
                                        )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    };

    return (
        <div className={styles.wrapper}>
            <FormProvider {...form}>
                <div className={styles.header}>
                    <div className={styles.text_wrapper}>
                        <Typography variant="h4">XEPPT Momentum</Typography>
                    </div>
                </div>
                <div className={styles.actions_content}>
                    <div className={styles.actions_wrapper}>
                        <Button
                            size="normal"
                            onClick={() => setActiveTab(EUserType.INDIVIDUAL)}
                            variant={
                                activeTab === EUserType.INDIVIDUAL ? 'primary' : 'outlined-dark'
                            }>
                            Personal
                        </Button>
                        <Button
                            size="normal"
                            onClick={() => setActiveTab(EUserType.BUSINESS)}
                            variant={
                                activeTab === EUserType.BUSINESS ? 'primary' : 'outlined-dark'
                            }>
                            Business
                        </Button>
                    </div>
                    <div className={styles.actions}>
                        {!isEdit && (
                            <Button
                                size="normal"
                                variant="outlined"
                                leftIcon="edit"
                                onClick={() => setIsEdit(true)}>
                                Edit
                            </Button>
                        )}
                        {isEdit && (
                            <>
                                <Button
                                    size="normal"
                                    variant="outlined-dark"
                                    onClick={() => {
                                        setIsEdit(false);
                                        form.reset(loyalty);
                                    }}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleSaveWithValidation}
                                    isLoading={isLoading}
                                    size="normal"
                                    variant="primary">
                                    Save Changes
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                <div className={styles.content}>
                    <div className={styles.block}>
                        <Typography className={styles.title} variant="h5">
                            Profile level settings
                        </Typography>
                        <div className={styles.divider} />
                        <div className={styles.row}>
                            {/* Render all user types but only show the active one */}
                            {userTypes.map((userType) => (
                                <div
                                    key={`profile-${userType}`}
                                    style={{
                                        display: activeTab === userType ? 'contents' : 'none'
                                    }}>
                                    <FormField
                                        name={`${userType}.points_to_${ELoyaltyLevel.B}`}
                                        renderComponent={(props) => (
                                            <Input
                                                readOnly={!isEdit}
                                                onlyNumbers
                                                className={styles.input}
                                                label="Number of points needed for Gold level"
                                                {...props}
                                            />
                                        )}
                                    />
                                    <FormField
                                        name={`${userType}.points_to_${ELoyaltyLevel.C}`}
                                        renderComponent={(props) => (
                                            <Input
                                                readOnly={!isEdit}
                                                onlyNumbers
                                                className={styles.input}
                                                label="Number of points needed for Platinum level"
                                                {...props}
                                            />
                                        )}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={styles.block}>
                        <Typography className={styles.title} variant="h5">
                            Period settings
                        </Typography>
                        <div className={styles.divider} />
                        <div className={styles.row}>
                            {userTypes.map((userType) => (
                                <div
                                    key={`period-${userType}`}
                                    style={{
                                        display: activeTab === userType ? 'contents' : 'none'
                                    }}>
                                    <FormField
                                        name={`${userType}.review_period`}
                                        renderComponent={(props) => (
                                            <Input
                                                readOnly={!isEdit}
                                                onlyNumbers
                                                className={styles.input}
                                                label="Number of days for the period"
                                                {...props}
                                            />
                                        )}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={styles.block}>
                        <Typography className={styles.title} variant="h5">
                            Reminder settings
                        </Typography>
                        <div className={styles.divider} />
                        <div className={styles.row}>
                            {userTypes.map((userType) => (
                                <div
                                    key={`reminder-${userType}`}
                                    style={{
                                        display: activeTab === userType ? 'contents' : 'none'
                                    }}>
                                    <FormField
                                        name={`${userType}.first_remind_in_days`}
                                        renderComponent={(props) => (
                                            <Input
                                                readOnly={!isEdit}
                                                onlyNumbers
                                                className={styles.input}
                                                label="First reminder email (before period ends)"
                                                prefix="days"
                                                {...props}
                                            />
                                        )}
                                    />
                                    <FormField
                                        name={`${userType}.last_remind_in_days`}
                                        renderComponent={(props) => (
                                            <Input
                                                readOnly={!isEdit}
                                                onlyNumbers
                                                className={styles.input}
                                                label="Last reminder email (before period ends)"
                                                prefix="days"
                                                {...props}
                                            />
                                        )}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={styles.block}>
                        <Typography className={styles.title} variant="h5">
                            Invite points
                        </Typography>
                        <div className={styles.divider} />
                        <div className={styles.row}>
                            {userTypes.map((userType) => (
                                <div
                                    key={`profile-${userType}`}
                                    style={{
                                        display: activeTab === userType ? 'contents' : 'none'
                                    }}>
                                    <FormField
                                        name={`${userType}.invite_sender_points`}
                                        renderComponent={(props) => (
                                            <Input
                                                readOnly={!isEdit}
                                                onlyNumbers
                                                className={styles.input}
                                                label="Sender"
                                                prefix="points"
                                                {...props}
                                            />
                                        )}
                                    />
                                    <FormField
                                        name={`${userType}.invite_receiver_points`}
                                        renderComponent={(props) => (
                                            <Input
                                                readOnly={!isEdit}
                                                onlyNumbers
                                                className={styles.input}
                                                label="Receiver"
                                                prefix="points"
                                                {...props}
                                            />
                                        )}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={styles.block}>
                        <Typography className={styles.title} variant="h5">
                            Earning points and fees settings
                        </Typography>
                        <div className={styles.divider} />
                        <div className={styles.actions_wrapper}>
                            <Button
                                size="normal"
                                onClick={() => setActiveLevel(ELoyaltyLevel.A)}
                                variant={
                                    activeLevel === ELoyaltyLevel.A ? 'primary' : 'outlined-dark'
                                }>
                                Silver
                            </Button>
                            <Button
                                size="normal"
                                onClick={() => setActiveLevel(ELoyaltyLevel.B)}
                                variant={
                                    activeLevel === ELoyaltyLevel.B ? 'primary' : 'outlined-dark'
                                }>
                                Gold
                            </Button>
                            <Button
                                size="normal"
                                onClick={() => setActiveLevel(ELoyaltyLevel.C)}
                                variant={
                                    activeLevel === ELoyaltyLevel.C ? 'primary' : 'outlined-dark'
                                }>
                                Platinum
                            </Button>
                        </div>
                        <Tabs
                            //@ts-ignore
                            onChange={(val) => setActiveTabFeePont(val)}
                            tabs={[
                                { value: 'point', label: 'Earning points' },
                                { value: 'fee', label: 'Fees' }
                            ]}
                            className={styles.tabs}
                            activeTab={activeTabFeePont}
                        />

                        {/* Render all possible combinations but only show the active one */}
                        {userTypes.map((userType) =>
                            loyaltyLevels.map((level) =>
                                feePointTypes.map((feePointType) => (
                                    <Fragment key={`${userType}-${level}-${feePointType}`}>
                                        {renderTable(userType, level, feePointType)}
                                    </Fragment>
                                ))
                            )
                        )}
                    </div>
                </div>
            </FormProvider>
        </div>
    );
};

export default LoyaltyLayout;
